import _ from 'lodash'
import moment from 'moment'
import api, { formCfg, listDehydrate } from './api'
import apiFactory from './api-factory'
import { dehydrate as userDehydrate } from './user'
import { dehydrate as profileDehydrate } from './profile'

function formParams(search, sorting, filter = {}, params) {
  const orderingMapping = {
    firstName: 'user__first_name',
    lastName: 'user__last_name',
    lastActiveAsc: 'last_active',
    lastActiveDesc: '-last_active',
  }

  const entryStateMapping = {
    withPublished: 'with_published',
    withUnpublished: 'with_unpublished',
    withoutEntries: 'without_entries',
  }

  const entryState = filter.entryState
    ? _.fromPairs(
        _.map(entryStateMapping, (value, key) => [
          value,
          _.includes(filter.entryState, key),
        ])
      )
    : null

  const activityDates = {
    withinLast30days: 30,
    withinLast60days: 60,
    withinLast90days: 90,
    moreThan90DaysAgo: 90,
  }

  let lastActivityData

  if (_.has(activityDates, filter.activity)) {
    lastActivityData = moment()
      .subtract(activityDates[filter.activity], 'days')
      .format('YYYY-MM-DD')
  }

  const hasLastLoginGte = _.includes(
    ['withinLast30days', 'withinLast60days', 'withinLast90days'],
    filter.activity
  )
  const hasLastLoginLte = filter.activity === 'moreThan90DaysAgo'

  let approved
  if (filter.approved && filter.approved !== 'showAll') {
    if (filter.approved === 'approved') {
      approved = 'True'
    } else {
      approved = 'False'
    }
  }

  const doiStatus =
    filter.doiStatus && filter.doiStatus !== 'showAll'
      ? filter.doiStatus
      : undefined

  return {
    search,
    ordering: orderingMapping[sorting],

    // Fetch only active users
    active: 'True',

    approved,
    entries_with_doi_status: doiStatus,
    entry_state: entryState,
    last_login__isnull: filter.activity === 'never' ? 'True' : undefined,
    last_login__gte: hasLastLoginGte ? lastActivityData : undefined,
    last_login__lte: hasLastLoginLte ? lastActivityData : undefined,
    has_answer_sets_for_poll:
      filter.poll !== 'showAll' ? filter.poll : undefined,
    regional_editor:
      filter.regionalEditor !== 'showAll' ? filter.regionalEditor : undefined,
    has_empty_map_requests: filter.entriesMissingMap ? 'True' : undefined,
    has_entries_with_suggested_tags: filter.entriesWithSuggestedTags
      ? 'True'
      : undefined,
    ...params,
  }
}

const expert = {
  ...apiFactory('/api/accounts/expert/', ['getList', 'getDetail', 'delete'], {
    hydrate,
    dehydrate,
  }),

  getMyList(search, sorting, filter, params, context) {
    const cfg = formCfg(formParams(search, sorting, filter, params), context)

    return api
      .get('/api/accounts/expert/my/', cfg)
      .then((response) => listDehydrate(response.data, dehydrate))
  },

  getPendingSignups({ limit, offset }) {
    const params = formCfg({
      ordering: '-user__date_joined',
      limit,
      offset,
    })

    return api
      .get('/api/accounts/expert/pending/', params)
      .then(({ data }) => listDehydrate(data, dehydrate))
  },

  getOtherList(search, sorting, filter, params, context) {
    const cfg = formCfg(formParams(search, sorting, filter, params), context)

    return api
      .get('/api/accounts/expert/other/', cfg)
      .then((response) => listDehydrate(response.data, dehydrate))
  },

  getUnassignedList(search, sorting, filter, params, context) {
    const cfg = formCfg(formParams(search, sorting, filter, params), context)

    return api
      .get('/api/accounts/expert/unassigned/', cfg)
      .then((response) => listDehydrate(response.data, dehydrate))
  },

  approve(pk, params, context) {
    return api.put(
      `/api/accounts/expert/${pk}/approve/`,
      formCfg(params, context)
    )
  },

  refuse(pk, params, context) {
    return api.put(
      `/api/accounts/expert/${pk}/refuse/`,
      formCfg(params, context)
    )
  },

  reassign(pk, editor, params, context) {
    return api.put(
      `/api/accounts/expert/${pk}/reassign/`,
      editor,
      formCfg(params, context)
    )
  },
}

export function dehydrate({
  pk,
  user,
  draft = 0,
  published = 0,
  approved,
  regionalEditor,
  requestedDoisCount = 0,
  contributedEntries = [],
  editor = { pk: null, type: null },
}) {
  return {
    pk,
    user: user ? userDehydrate(user) : null,
    profile: user && user.profile ? profileDehydrate(user.profile) : null,
    draft,
    published,
    approved,
    contributedEntries,
    requestedDoisCount,
    regionalEditor,
    editor,
  }
}

export function hydrate({
  pk,
  regionalEditor,
  editor = { pk: null, type: null },
}) {
  return {
    pk,
    regionalEditor,
    editor,
  }
}

export default expert
